import { AnimatePresence, motion } from 'framer-motion'
import type {
  CSSProperties,
  FC,
  MouseEventHandler,
  PropsWithChildren,
} from 'react'
import { createContext, useContext } from 'react'
import { useRef } from 'react'
import { Portal } from 'react-portal'
import { useKeyPressEvent } from 'react-use'
import { getBrandZIndex } from '../../themes/zIndex'
import { cn } from '@jotta/utils/css'

export interface ModalProps {
  /** Modal open state */
  open?: boolean
  /** Callback to run when close button or background is clicked */
  onClose?: () => void
  disableBlur?: boolean
  style?: CSSProperties
}

export const ModalContext = createContext<Element | null>(null)

export const Modal: FC<PropsWithChildren<ModalProps>> = ({
  open = false,
  disableBlur,
  children,
  onClose,
  ...props
}) => {
  const closeHandler = () => {
    if (onClose) {
      onClose()
      return
    }
    console.warn('No close handler registered for dialog')
  }
  const ref = useRef<HTMLDivElement>(null)
  const clickAwayHandler: MouseEventHandler = e => {
    const clickedEl = e.target as HTMLElement
    if (ref.current && ref.current === clickedEl) {
      closeHandler()
    }
  }
  useKeyPressEvent('Escape', closeHandler)

  const node = useContext(ModalContext)

  return (
    <AnimatePresence>
      {open && (
        <Portal node={node}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={clickAwayHandler}
            ref={ref}
            className={cn(
              'fixed bottom-0 left-0 right-0 top-0 flex w-full bg-[rgba(0,0,0,0.5)]',
              {
                'backdrop-blur': !disableBlur,
              },
            )}
            style={{
              zIndex: getBrandZIndex('modal'),
            }}
            {...props}
          >
            {children}
          </motion.div>
        </Portal>
      )}
    </AnimatePresence>
  )
}
